import React, { useEffect, useState } from 'react';
import { ClipboardList, Send, Clock, CheckCircle2, AlertTriangle, FileEdit, PlusCircle, LayoutList, FileText, History, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';
import { surveyService } from '../../../services/surveyService';
import { Card, StatusBadge, Button, Spinner } from '../../../shared/sharedComponent';
import './Dashboard.css';

const STAT_CARDS = [
  { key: 'total',       label: 'Total Surveys', color: 'navy',  Icon: ClipboardList  },
  { key: 'sent',        label: 'Submitted',     color: 'blue',  Icon: Send           },
  { key: 'in_progress', label: 'In Progress',   color: 'amber', Icon: Clock          },
  { key: 'submitted',   label: 'Completed',     color: 'green', Icon: CheckCircle2   },
  { key: 'overdue',     label: 'Pending',       color: 'red',   Icon: AlertTriangle  },
  { key: 'draft',       label: 'Drafts',        color: 'gray',  Icon: FileEdit       },
];

const FORM_TYPES = [
  {
    code: 'MQ1',
    title: 'Survey of Manufacturing',
    subtitle: 'Quarterly',
    sector: 'manufacturing',
    color: '#003F82',
    fields: ['Employment', 'Current Expenditure', 'Capital Expenditure', 'Production'],
  },
  {
    code: 'WRTQ1',
    title: 'Survey of Wholesale & Retail Trade',
    subtitle: 'Quarterly',
    sector: 'wholesale_retail',
    color: '#065F46',
    fields: ['Employment', 'Current Expenditure', 'Capital Expenditure', 'Revenue'],
  },
  {
    code: 'MM1',
    title: 'Survey of Manufacturing',
    subtitle: 'Monthly',
    sector: 'manufacturing',
    color: '#7C3AED',
    fields: ['Production (incl. exports)'],
  },
  {
    code: 'Monthly_WRT',
    title: 'Survey of Wholesale & Retail Trade',
    subtitle: 'Monthly',
    sector: 'wholesale_retail',
    color: '#B45309',
    fields: ['Turnover / Revenue'],
  },
  {
    code: 'GQ1',
    title: 'Generic Questionnaire',
    subtitle: 'Quarterly',
    sector: 'all',
    color: '#0E7490',
    fields: ['Employment', 'Sales Value'],
  },
];

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats]               = useState(null);
  const [recentSurveys, setRecentSurveys] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [establishments, setEstablishments] = useState([]);
  const [quarters, setQuarters]         = useState([]);
  const [months, setMonths]             = useState([]);
  const [filters, setFilters]           = useState({ sector: '', establishment_id: '', quarter_id: '', month_id: '', form_code: '' });

  useEffect(() => {
    const load = async () => {
      try {
        const params = new URLSearchParams(
          Object.fromEntries(Object.entries(filters).filter(([, v]) => v))
        ).toString();

        const [statsRes, surveysRes, estRes] = await Promise.all([
          api.get(`/surveys/stats${params ? '?' + params : ''}`),
          surveyService.getAll({ page: 1, limit: 5, view: 'active' }),
          api.get('/establishments'),
        ]);
        const statsData = statsRes.data.data;
        setStats(statsData);
        if (statsData?.quarters) setQuarters(statsData.quarters);
        if (statsData?.months)   setMonths(statsData.months);
        setRecentSurveys(surveysRes.data.data);
        setEstablishments(estRes.data.data || []);
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filters]);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Spinner size="lg" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard animate-fade-in">
      {/* ── Welcome Banner ── */}
      <div className="dashboard__welcome">
        <div className="dashboard__welcome-text">
          <h1>{greeting()}, {user?.firstName} </h1>
          <p>Here's what's happening with your surveys today.</p>
        </div>
        <Button
          variant="primary"
          onClick={() => navigate('/internal/surveys/create')}
          icon={
            <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
          }
        >
          Create Survey
        </Button>
      </div>

      {/* ── KPI Filters ── */}
      <div className="dashboard__kpi-filters">
        <select className="dashboard__kpi-filter" value={filters.sector}
          onChange={(e) => setFilters(f => ({ ...f, sector: e.target.value, establishment_id: '' }))}>
          <option value="">All Industries</option>
          {[...new Set(establishments.map(e => e.sector_type).filter(Boolean))].map(s => (
            <option key={s} value={s}>
              {s.replace(/_/g, ' ').replace(/\w/g, c => c.toUpperCase())}
            </option>
          ))}
        </select>

        <select className="dashboard__kpi-filter" value={filters.establishment_id}
          onChange={(e) => setFilters(f => ({ ...f, establishment_id: e.target.value }))}>
          <option value="">All data sources</option>
          {establishments
            .filter(e => !filters.sector || e.sector_type === filters.sector)
            .map(e => <option key={e.establishment_id} value={e.establishment_id}>{e.establishment_name}</option>)
          }
        </select>

        <select className="dashboard__kpi-filter" value={filters.quarter_id}
          onChange={(e) => setFilters(f => ({ ...f, quarter_id: e.target.value, month_id: '' }))}>
          <option value="">All quarters</option>
          {quarters.map(q => <option key={q.quarter_id} value={q.quarter_id}>{q.quarter_name}</option>)}
        </select>

        <select className="dashboard__kpi-filter" value={filters.month_id}
          onChange={(e) => setFilters(f => ({ ...f, month_id: e.target.value, quarter_id: '' }))}>
          <option value="">All months</option>
          {months.map(m => <option key={m.month_id} value={m.month_id}>{m.month_name}</option>)}
        </select>

        <select className="dashboard__kpi-filter" value={filters.form_code}
          onChange={(e) => setFilters(f => ({ ...f, form_code: e.target.value }))}>
          <option value="">All forms</option>
          {FORM_TYPES.map(f => <option key={f.code} value={f.code}>{f.code}</option>)}
        </select>

        {Object.values(filters).some(v => v) && (
          <button className="dashboard__kpi-filter-clear"
            onClick={() => setFilters({ sector: '', establishment_id: '', quarter_id: '', month_id: '', form_code: '' })}>
            ✕ Clear filters
          </button>
        )}
      </div>

      {/* ── Stat Cards ── */}
      <div className="dashboard__stats">
        {STAT_CARDS.map((s, i) => (
          <div
            key={s.key}
            className={`stat-card stat-card--${s.color}`}
            style={{ animationDelay: `${i * 60}ms` }}
            onClick={() => navigate('/internal/surveys')}
          >
            {/* Bubble decorations */}
            <span className="stat-card__bubble stat-card__bubble--1" />
            <span className="stat-card__bubble stat-card__bubble--2" />
            {/* Icon */}
            <div className="stat-card__icon-wrap">
              <s.Icon size={20} strokeWidth={1.8} />
            </div>
            {/* Value */}
            <div className="stat-card__value">
              {stats?.statusCounts?.[s.key] ?? 0}
            </div>
            {/* Label */}
            <span className="stat-card__label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── Quick Actions ── */}
      <div className="dashboard__quick-actions-bar">
        <h3 className="dashboard__section-head__title">Quick Actions</h3>
        <div className="dashboard__quick-actions-row">
          {[
            { Icon: PlusCircle,  color: '#d97706', title: 'New Survey',     path: '/internal/surveys/create' },
            { Icon: LayoutList,  color: '#0284c7', title: 'Active Surveys', path: '/internal/surveys' },
            { Icon: FileText,    color: '#6b7280', title: 'Draft Surveys',  path: '/internal/surveys/drafts' },
            { Icon: History,     color: '#7c3aed', title: 'Past Surveys',   path: '/internal/surveys/past' },
            { Icon: Users,       color: '#16a34a', title: 'Respondents', path: '/internal/providers' },
          ].map((a) => (
            <div key={a.path} className="dashboard__quick-action-btn" onClick={() => navigate(a.path)}>
              <div className="dashboard__quick-action-btn__icon" style={{ color: a.color }}>
                <a.Icon size={24} strokeWidth={1.5} />
              </div>
              <span className="dashboard__quick-action-btn__label">{a.title}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="dashboard__grid">
        {/* ── Recent Surveys ── */}
        <div className="dashboard__recent">
          <div className="dashboard__section-head">
            <h3>Recent Surveys</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate('/internal/surveys')}>
              View all →
            </Button>
          </div>
          {recentSurveys.length === 0 ? (
            <Card>
              <div className="dashboard__empty">
                <span>No surveys yet.</span>
                <Button variant="secondary" size="sm" onClick={() => navigate('/internal/surveys/create')}>
                  Create your first survey
                </Button>
              </div>
            </Card>
          ) : (
            <Card padding={false}>
              <div className="survey-table">
                <div className="survey-table__head">
                  <span>Survey</span>
                  <span>Data Source</span>
                  <span>Period</span>
                  <span>Status</span>
                  <span>Dispatched</span>
                </div>
                {recentSurveys.map((s) => (
                  <div
                    key={s.survey_id}
                    className="survey-table__row"
                    onClick={() => navigate(`/internal/surveys/${s.survey_id}`)}
                  >
                    <div className="survey-table__col survey-table__col--main">
                      <span className="survey-table__form-code">{s.survey_form_code}</span>
                      <span className="survey-table__title">{s.survey_title}</span>
                    </div>
                    <div className="survey-table__col">
                      <span className="survey-table__establishment">{s.establishment_name || '—'}</span>
                    </div>
                    <div className="survey-table__col">
                      <span className="survey-table__period">
                        {s.quarter_name || s.month_name || '—'}
                      </span>
                    </div>
                    <div className="survey-table__col">
                      <StatusBadge status={s.survey_status} />
                    </div>
                    <div className="survey-table__col">
                      <span className="survey-table__dispatched">{s.dispatched_count ?? 0}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* ── Form Types Reference ── */}
        <div className="dashboard__forms">
          <div className="dashboard__section-head">
            <h3>Available Survey Forms</h3>
          </div>
          <div className="form-cards">
            {FORM_TYPES.filter(f => !user?.sector_type || f.sector === 'all' || f.sector === user.sector_type).map((f) => (
              <div key={f.code} className="form-card" style={{ '--form-color': f.color }}>
                <div className="form-card__header">
                  <span className="form-card__code">{f.code}</span>
                  <span className="form-card__freq">{f.subtitle}</span>
                </div>
                <p className="form-card__title">{f.title}</p>
                <p className="form-card__sector">
                  {f.sector === 'manufacturing' ? 'Manufacturing'
                    : f.sector === 'wholesale_retail' ? 'Wholesale & Retail'
                    : 'All Sectors'}
                </p>
                <ul className="form-card__sections">
                  {f.fields.map((field) => (
                    <li key={field}>
                      <svg viewBox="0 0 16 16" fill="currentColor" width="10" height="10">
                        <path d="M6.5 14.5l-5-5 1.5-1.5 3.5 3.5 7.5-7.5 1.5 1.5z"/>
                      </svg>
                      {field}
                    </li>
                  ))}
                </ul>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/internal/surveys/create?form=${f.code}`)}
                  className="form-card__btn"
                >
                  Create →
                </Button>
              </div>
            ))}
          </div>
        </div>


      </div>
    </div>
  );
};

export default Dashboard;